import { Copy, ExternalLink } from "lucide-react";

import { toast } from "@/shared/hooks/use-toast";

import { isGenericDraft, type AcqDeliverable } from "../../api/acqService";

const KIND_LABEL: Record<AcqDeliverable["kind"], string> = {
  answer_draft: "回答稿",
  opener: "开场白",
  outreach: "私信话术",
  // ★ 0917 批次三(契约 §14.6):第一方线的产物。不叫「开场白」是因为它不是开场 ——
  //   对方已经在跟你说话了,这是**接着说的下一句**。
  reply_draft: "回复草稿",
};

/** 取值域开放(ai 侧可能先加新 kind 再改前端),查不到原样显示 code。 */
function kindLabel(kind: string): string {
  return KIND_LABEL[kind as AcqDeliverable["kind"]] ?? kind;
}

/**
 * `targetUrl` 那个链接叫什么。
 * ★ 第一方线跳的是**自家客服会话**(契约 §14.1),写「打开那一页」会让人以为要开外网 ——
 *   而这一下其实是回自己的客服台,把刚写好的那句话发出去。
 * ★ 其余 kind 仍是那句通用的:问题页 / 作者主页 / 公告原文都归它,措辞多一种分不清好处。
 */
function targetLabel(kind: string): string {
  return kind === "reply_draft" ? "打开会话" : "打开那一页";
}

export async function copyText(text: string, what: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast({ title: `${what}已复制` });
  } catch {
    toast({ title: "复制失败，请手动选中复制", variant: "destructive" });
  }
}

/**
 * 接手之后的产物面板(契约 §12.4 / §12.5)。
 *
 * ★★ 这一块是 v2「接手免费」这条承诺的**可见形态**:用户点一下、没花钱,
 *   手上就有了一段能直接贴出去的话。它必须是**全文**,不许折叠成两行加一个「展开」——
 *   折起来的东西看起来像广告位,而这正是我们想让他相信"白给的也是真东西"的那一刻。
 * ★★ `[通用稿]` 打头时要**明说是通用稿**(KB 为空时 ai 侧这么标)。
 *   不说的话,用户会以为这段话是照着他自己的资料写的,照着发出去才发现不对 ——
 *   那比不给他这段话坏得多。
 */
export function DeliverablePanel({ deliverable }: { deliverable: AcqDeliverable }) {
  const generic = isGenericDraft(deliverable);

  return (
    <section className="rounded-lg border bg-muted/30 px-3 py-2.5">
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <span className="rounded bg-primary/10 px-1.5 py-px text-2xs font-bold text-primary">
          {kindLabel(deliverable.kind)}
        </span>
        <div className="flex items-center gap-3">
          {deliverable.targetUrl && (
            <a
              href={deliverable.targetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-2xs font-semibold text-primary hover:underline"
            >
              {targetLabel(deliverable.kind)}
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <button
            type="button"
            onClick={() => void copyText(deliverable.text, kindLabel(deliverable.kind))}
            className="inline-flex items-center gap-1 text-2xs font-semibold text-primary hover:underline"
          >
            <Copy className="h-3 w-3" />
            复制
          </button>
        </div>
      </div>

      {generic && (
        <p className="mb-1.5 rounded border border-dashed border-warning/50 bg-warning/5 px-2 py-1 text-2xs leading-relaxed text-warning">
          知识库为空，这是通用稿 —— 里面没有你自己的案例和口径，发之前改一改。
        </p>
      )}

      {/* ★ `whitespace-pre-wrap`:回答稿是**分段**写的(ai 侧用 \n 断行),
          不保留换行会糊成一坨,而一坨字没人会复制走。 */}
      <p className="whitespace-pre-wrap text-xs leading-relaxed text-foreground/90">
        {deliverable.text}
      </p>
    </section>
  );
}
